import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { IS_AWS_ENV, IS_MCDAN_RESEARCH_ENV } from "constants/app.constants";
import {
  MY_FOCUS_FIELDS,
  ORG_FOCUS_FIELDS,
  ORG_GEO_FIELDS,
  ORG_GEO_SIMS,
  PROJECT_FOCUS_FIELDS,
  SHAPEFILE_FIELDS
} from "constants/settings.constants";
import { AppThunk } from "store/store";
import { getPerformanceRatioDisplayName } from "utils/data-table/getProductDisplayName";
import { getProductLabel } from "utils/getProductLabel";

import { BetaFeatures, hasBetaFeature } from "hooks/useBetaFeatures";

import { EntityKind } from "models/entityKind";
import {
  IGroupBy,
  IGroupByListColumn,
  IGroupByListItem,
  IGroupByState
} from "models/groupBy";

import formatPerformanceDateRange from "components/groupBy/helpers/formatPerformanceDateRange";
import { getUserSettingsDefaults } from "components/user/queries";

import { fetchOrgDefinedColumns } from "../../../components/geom-bin/helpers/fetchOrgDefinedColumns";
import { fetchProjectDefinedColumns } from "../../../components/geom-bin/helpers/fetchProjectDefinedColumns";
import { fetchUserDefinedColumns } from "../../../components/geom-bin/helpers/fetchUserDefinedColumns";
import { getSessionItem, setSessionItem } from "../../utils/sessionStorageHelper";

function getSessionGroupBy(): Maybe<IGroupBy> {
  return getSessionItem<Maybe<IGroupBy>>("globalGroupBy", undefined);
}

function getSessionFacilityFocus(): Maybe<IGroupBy> {
  return getSessionItem<Maybe<IGroupBy>>("globalFacilityFocus", undefined);
}

const updateRecentFocus = (
  focusList: IGroupBy[],
  currentGb: IGroupBy,
  newGb: IGroupBy
) => {
  let index = focusList.findIndex(
    (gb) => gb.property === newGb.property || gb.property === currentGb.property
  );

  while (index >= 0) {
    focusList.splice(index, 1);
    index = focusList.findIndex(
      (gb) => gb.property === newGb.property || gb.property === currentGb.property
    );
  }
  // Add previous group by to recent focus
  return [currentGb, ...focusList].slice(0, 6);
};

// Computed at module load: whether the initial globalGroupBy came from sessionStorage.
// A `let` so resetGlobalGroupByRestoredFromSessionFlag can reset it on sign-out.
export let GLOBAL_GROUP_BY_RESTORED_FROM_SESSION = getSessionGroupBy() !== undefined;

// Hardcodes false: sign-out's resetSlices runs before sessionStorage.clear(), so
// re-deriving here would still see the outgoing user's item.
export function resetGlobalGroupByRestoredFromSessionFlag(): void {
  GLOBAL_GROUP_BY_RESTORED_FROM_SESSION = false;
}

const DEFAULT_GLOBAL_GROUP_BY: IGroupBy = {
  title: "Resource Play",
  property: "Header.ResourcePlay",
  groupByField: "Header.ResourcePlay",
  pdenSource: "Public",
  canBin: false,
  dataType: "Text",
  categoryId: 1,
  tooltip: "",
  display: "",
  entityKind: EntityKind.Well,
  bin: {
    BinSize: "",
    MinSize: "",
    MaxBins: "",
    MaxBinsSortOrder: "WellCount",
    GreaterThan: "",
    BinType: "BinSize",
    LessThan: ""
  }
  //color: "#F3C300",
};

export const initialGroupByState: IGroupByState = {
  categoryList: [],
  facilityFields: [],
  presetList: [],
  recentWellFocus: [],
  recentFacilityFocus: [],
  // @deprecated — migrated to hoverDataPointAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle.
  hoverDataPoint: undefined,
  // @deprecated — migrated to hoverLegendBaseAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle.
  hoverLegendItem: "",
  // @deprecated — migrated to hoverLegendBaseAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle.
  hoverLegendGroup: "",
  // @deprecated — migrated to hoverLegendBaseAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle.
  hoverLegendId: "",
  // @deprecated — migrated to hoverLegendBaseAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle.
  hoverLegendItemUnfiltered: "",
  groupByTitle: undefined,
  cumPlusForecast: false,
  selectedGroups: [],
  selectedFacilityGroups: [],
  hasCheckedBins: false,
  hasCheckedBinsFacility: false,
  placeholderBinsEnabled: false,
  selectedProjectIdForColumns: null,
  isLoadingProjectColumns: false,
  isRefreshingCategoriesWell: false,
  isRefreshingCategoriesFacility: false,
  activeFocus: EntityKind.Well,
  globalGroupBy: getSessionGroupBy() ?? DEFAULT_GLOBAL_GROUP_BY,
  globalFacilityFocus: getSessionFacilityFocus() ?? {
    title: "Facility Operator",
    property: "Header.OperatorName",
    groupByField: "Header.OperatorName",
    pdenSource: "Public",
    canBin: false,
    dataType: "Text",
    categoryId: 1,
    tooltip: "",
    display: "",
    entityKind: EntityKind.Facility,
    bin: {
      BinSize: "",
      MinSize: "",
      MaxBins: "",
      MaxBinsSortOrder: "WellCount",
      GreaterThan: "",
      BinType: "BinSize",
      LessThan: ""
    }
    //color: "#F3C300",
  }
};
const setGlobalGroupByBaseOnEntityKind = (state, gb: IGroupBy) => {
  if (gb.entityKind === EntityKind.Well) {
    state.globalGroupBy = gb;
  } else {
    state.globalFacilityFocus = gb;
  }
};

const groupBySlice = createSlice({
  name: "groupby",
  initialState: initialGroupByState,
  reducers: {
    setCategoryList(state, action: PayloadAction<IGroupByListItem[]>) {
      state.categoryList = action.payload;
    },
    setSelectedGroups(state, action: PayloadAction<string[]>) {
      state.selectedGroups = action.payload;
    },
    setHasCheckedBins(state, action: PayloadAction<boolean>) {
      state.hasCheckedBins = action.payload;
    },
    setHasCheckedBinsFacility(state, action: PayloadAction<boolean>) {
      state.hasCheckedBinsFacility = action.payload;
    },
    setPlaceholderBinsEnabled(state, action: PayloadAction<boolean>) {
      state.placeholderBinsEnabled = action.payload;
    },
    setSelectedFacilityGroups(state, action: PayloadAction<string[]>) {
      state.selectedFacilityGroups = action.payload;
    },
    setUserDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.categoryList.findIndex((f) => f.name === MY_FOCUS_FIELDS);
      if (udcIndex < 0) {
        return;
      }
      state.categoryList[udcIndex].columns = action.payload;
    },
    setOrgDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.categoryList.findIndex((f) => f.name === ORG_FOCUS_FIELDS);
      if (udcIndex < 0) {
        return;
      }
      state.categoryList[udcIndex].columns = action.payload;
    },
    setProjectDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.categoryList.findIndex(
        (f) => f.name === PROJECT_FOCUS_FIELDS
      );
      if (udcIndex < 0) {
        return;
      }
      state.categoryList[udcIndex].columns = action.payload;
    },
    setPresetList(state, action) {
      state.presetList = action.payload;
    },
    setGroupByTitle(state, action: PayloadAction<Maybe<string>>) {
      state.groupByTitle = action.payload;
    },
    setFacilityFields(state, action: PayloadAction<IGroupByListItem[]>) {
      state.facilityFields = action.payload;
    },
    setFacilityUserDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.facilityFields.findIndex((f) => f.name === MY_FOCUS_FIELDS);
      if (udcIndex < 0) {
        return;
      }
      state.facilityFields[udcIndex].columns = action.payload;
    },
    setFacilityOrgDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.facilityFields.findIndex((f) => f.name === ORG_FOCUS_FIELDS);
      if (udcIndex < 0) {
        return;
      }
      state.facilityFields[udcIndex].columns = action.payload;
    },
    setFacilityProjectDefinedColumns(state, action: PayloadAction<IGroupByListColumn[]>) {
      const udcIndex = state.facilityFields.findIndex(
        (f) => f.name === PROJECT_FOCUS_FIELDS
      );
      if (udcIndex < 0) {
        return;
      }
      state.facilityFields[udcIndex].columns = action.payload;
    },
    setGlobalGroupBy(state, action: PayloadAction<IGroupBy>) {
      if (action.payload.entityKind === EntityKind.Well) {
        setSessionItem("globalGroupBy", action.payload);
      } else {
        setSessionItem("globalFacilityFocus", action.payload);
      }
      const currentGb =
        action.payload.entityKind === EntityKind.Well
          ? state.globalGroupBy
          : state.globalFacilityFocus;
      if (action.payload.property === currentGb.property) {
        //only update groupby state if property is the same
        //skip the recent focus list
        setGlobalGroupByBaseOnEntityKind(state, action.payload);
        return;
      }

      if (action.payload.entityKind === EntityKind.Well) {
        state.recentWellFocus = updateRecentFocus(
          state.recentWellFocus,
          currentGb,
          action.payload
        );
      } else {
        state.recentFacilityFocus = updateRecentFocus(
          state.recentFacilityFocus,
          currentGb,
          action.payload
        );
      }
      setGlobalGroupByBaseOnEntityKind(state, action.payload);
    },
    setCumPlusForecast(state, action: PayloadAction<boolean>) {
      state.cumPlusForecast = action.payload;
    },
    setSelectedProjectIdForColumns(state, action: PayloadAction<Maybe<string>>) {
      state.selectedProjectIdForColumns = action.payload;
    },
    setIsLoadingProjectColumns(state, action: PayloadAction<boolean>) {
      state.isLoadingProjectColumns = action.payload;
    },
    setIsRefreshingCategoriesWell(state, action: PayloadAction<boolean>) {
      state.isRefreshingCategoriesWell = action.payload;
    },
    setIsRefreshingCategoriesFacility(state, action: PayloadAction<boolean>) {
      state.isRefreshingCategoriesFacility = action.payload;
    },
    setActiveFocus(state, action: PayloadAction<EntityKind>) {
      state.activeFocus = action.payload;
    },
    // The initial focus is the outgoing user's session snapshot, so the next user
    // would inherit it and never get the fresh-session Max Bins default.
    resetGroupBySlice: () =>
      IS_AWS_ENV
        ? { ...initialGroupByState, globalGroupBy: DEFAULT_GLOBAL_GROUP_BY }
        : initialGroupByState
  }
});

const rootUrl = process.env.REACT_APP_COLUMN_SET_SERVICE;

export const {
  setCategoryList,
  setGlobalGroupBy,
  setHasCheckedBins,
  setHasCheckedBinsFacility,
  setPlaceholderBinsEnabled,
  setGroupByTitle,
  setCumPlusForecast,
  setUserDefinedColumns,
  setOrgDefinedColumns,
  setProjectDefinedColumns,
  setSelectedGroups,
  setSelectedFacilityGroups,
  setFacilityFields,
  setFacilityUserDefinedColumns,
  setFacilityOrgDefinedColumns,
  setFacilityProjectDefinedColumns,
  setSelectedProjectIdForColumns,
  setIsLoadingProjectColumns,
  setIsRefreshingCategoriesWell,
  setIsRefreshingCategoriesFacility,
  setActiveFocus,
  resetGroupBySlice
} = groupBySlice.actions;

const latestCategoryListRequestId: Record<string, number> = {
  [EntityKind.Well]: 0,
  [EntityKind.Facility]: 0
};

/**
 * Rewrites one category's columns against the list that is in the store right
 * now. Callers that read the list, splice their category into a copy and
 * dispatch the whole thing lose every change that landed in between — two
 * shapefile columns resolving concurrently drop one of the two. Reading state
 * at dispatch time instead of from a captured copy keeps both.
 */
export const updateCategoryColumns =
  (
    entityKind: EntityKind,
    categoryName: string,
    updateColumns: (columns: IGroupByListColumn[]) => Maybe<IGroupByListColumn[]>
  ): AppThunk =>
  (dispatch, getState) => {
    const state = getState().groupBy;
    const list =
      entityKind === EntityKind.Well ? state.categoryList : state.facilityFields;
    const index = list.findIndex((item) => item.name === categoryName);
    if (index < 0) {
      return;
    }
    const columns = updateColumns(list[index].columns ?? []);
    // No change to make (the column is already there) — skip the dispatch so
    // nothing re-renders on it.
    if (!columns) {
      return;
    }
    const next = [...list];
    next[index] = { ...list[index], columns };
    dispatch(
      entityKind === EntityKind.Well ? setCategoryList(next) : setFacilityFields(next)
    );
  };

/** Replaces a category's columns, rebased on the freshest list. */
export const setCategoryColumns = (
  entityKind: EntityKind,
  categoryName: string,
  columns: IGroupByListColumn[]
): AppThunk => updateCategoryColumns(entityKind, categoryName, () => columns);

/** Appends one column to a category unless its property is already present. */
export const mergeColumnIntoCategory = (
  entityKind: EntityKind,
  categoryName: string,
  column: IGroupByListColumn
): AppThunk =>
  updateCategoryColumns(entityKind, categoryName, (columns) =>
    columns.some((existing) => existing.property === column.property)
      ? null
      : [...columns, column]
  );

const applyUnitToColumnTitle = (
  column: IGroupByListColumn,
  unitType: number
): IGroupByListColumn => {
  const displayUnit = column.units?.[unitType.toString()] || column.defaultUnit;

  if (!displayUnit) return column;

  if (column.title.includes(`(${displayUnit})`)) {
    return column;
  }

  if (column.title.includes("(") && column.title.includes(")")) {
    return {
      ...column,
      title: column.title.replace(/\([^)]*\)/, `(${displayUnit})`)
    };
  }

  return {
    ...column,
    title: `${column.title} (${displayUnit})`
  };
};

const findMatchingColumnInCategories = (
  categories: IGroupByListItem[],
  property: string
): Maybe<IGroupByListColumn> => {
  for (const category of categories) {
    const matchingColumn = category.columns?.find((col) => col.property === property);
    if (matchingColumn) {
      return matchingColumn;
    }
  }
  return undefined;
};

const shouldUpdateGlobalGroupBy = (
  currentGroupBy: { title?: string; units?: unknown; defaultUnit?: string },
  matchingColumn: { title?: string; units?: unknown; defaultUnit?: string }
): boolean => {
  return (
    matchingColumn.title !== currentGroupBy.title ||
    matchingColumn.units !== currentGroupBy.units ||
    matchingColumn.defaultUnit !== currentGroupBy.defaultUnit
  );
};

/**
 * Rebuilds the groupBy title from the fresh column title (with correct unit),
 * re-appending the product label and performance date range if applicable.
 */
const buildEnrichedTitle = (
  baseTitle: string,
  matchingColumn: IGroupByListColumn,
  currentGroupBy: IGroupBy,
  entityKind: EntityKind
): string => {
  let title = baseTitle;

  if (matchingColumn.product) {
    const productLabel = getPerformanceRatioDisplayName(
      getProductLabel(matchingColumn.product, entityKind)
    );
    if (!title.toLowerCase().includes(`(${productLabel.toLowerCase()})`)) {
      title += ` (${productLabel})`;
    }
  }

  if (currentGroupBy.performanceDateRange && matchingColumn.subgroup === "Date Range") {
    const dateStr = formatPerformanceDateRange(
      currentGroupBy.performanceDateRange.startDate,
      currentGroupBy.performanceDateRange.endDate
    );
    if (dateStr) {
      title += ` ${dateStr}`;
    }
  }

  return title;
};

export const getCategoryList =
  (kind: EntityKind = EntityKind.Well, unitType: number = 0): AppThunk =>
  async (dispatch, getState) => {
    // Generate a unique request ID to handle race conditions
    const currentRequestId = ++latestCategoryListRequestId[kind];

    // Use entity-specific refresh flag to avoid conflicts when both are called simultaneously
    const setRefreshing =
      kind === EntityKind.Well
        ? setIsRefreshingCategoriesWell
        : setIsRefreshingCategoriesFacility;
    dispatch(setRefreshing(true));
    try {
      const res = await axios.get(`${rootUrl}/column/new?entityKind=${kind.toString()}`);
      const list: IGroupByListItem[] = res.data;
      const state = getState();

      const orgList = await fetchOrgDefinedColumns(kind);
      const userSettings = await getUserSettingsDefaults();

      // Unit type reference: 0 = Hybrid, 1 = SI, 2 = Imperial
      // Apply correct unit suffix to all column titles based on user's preference
      list.forEach((category) => {
        if (category.columns) {
          category.columns = category.columns.map((column) =>
            applyUnitToColumnTitle(column, unitType)
          );
        }
      });

      const hasFeature = (feature: BetaFeatures): boolean => {
        return hasBetaFeature(state.auth?.user?.organization?.betaFeatures, feature);
      };

      const canSeeUnpublishedOrgFields = (): boolean => {
        if (hasFeature("User Feature Flags")) {
          // If org requires user feature flag, check if user has the feature flag
          return userSettings.data?.FeatureFlag?.OrgFieldsTester?.enabled ?? false;
        } else {
          return false;
        }
      };

      if (!canSeeUnpublishedOrgFields()) {
        // Filter out unpublished org fields
        orgList.columns = orgList.columns.filter((item) => item.published !== false);
      }

      if (orgList && kind === EntityKind.Well) {
        const orgGeoFieldsEabled = (): boolean => {
          return hasFeature("Org Geo Fields");
        };

        // Collect all the geo fields from the org list
        const orgGeoFields = orgList.columns.filter((item) =>
          item.property.startsWith("Org_Focus_Fields_Geo_")
        );

        // Collect all the geo sims from the org list
        const orgGeoSims = orgList.columns.filter((item) =>
          item.property.startsWith("Org_Focus_Fields_Sim_Geo_")
        );

        // Remove the geo fields from the org list
        orgList.columns = orgList.columns.filter(
          (item) =>
            !orgGeoFields.some((field) => item.property === field.property) &&
            !orgGeoSims.some((sim) => item.property === sim.property)
        );

        // Add 3D Geo Model category
        if (orgGeoFieldsEabled()) {
          list.push(orgList);

          list.push({
            name: ORG_GEO_FIELDS,
            hexColor: "#136d8b",
            sortOrder: 100,
            property: "",
            section: "Organization",
            columns: orgGeoFields,
            id: ORG_GEO_FIELDS
          });

          // TODO: check if org has geo sims
          list.push({
            name: ORG_GEO_SIMS,
            hexColor: "#ffdf00",
            sortOrder: 100,
            property: "",
            section: "Organization",
            columns: orgGeoSims,
            id: ORG_GEO_SIMS
          });
        } else {
          list.push(orgList);
        }
      } else {
        list.push(orgList);
      }

      if (state.auth?.user?.organization?.allowUserDefinedFields) {
        const userList = await fetchUserDefinedColumns(kind);
        if (userList) {
          list.push(userList);
        }

        const selectedProjectId = state.groupBy.selectedProjectIdForColumns;
        const projectColumns = selectedProjectId
          ? await fetchProjectDefinedColumns(kind, selectedProjectId)
          : [];

        list.push({
          name: "Project Fields",
          hexColor: "#7B68EE",
          sortOrder: 100,
          property: "",
          section: "Organization",
          columns: projectColumns,
          id: "Project Fields"
        });
      }

      list.push({
        name: SHAPEFILE_FIELDS,
        hexColor: "#8B4513",
        sortOrder: 100,
        property: "",
        section: "Organization",
        columns: [],
        id: SHAPEFILE_FIELDS
      });

      const schema = state.app.syncWells?.schema;
      if (IS_MCDAN_RESEARCH_ENV && schema) {
        const schemaDefinedColumnResponse = await axios.get(
          `${rootUrl}/schema-defined-columns/${schema}`
        );
        if (schemaDefinedColumnResponse.status === 200) {
          list.push(schemaDefinedColumnResponse.data);
        }
      }
      if (list?.length > 0) {
        if (kind === EntityKind.Well) {
          dispatch(setCategoryList(list));

          // Skip sync if a newer request has been initiated (prevents race conditions)
          const isStaleRequest = currentRequestId !== latestCategoryListRequestId[kind];
          if (isStaleRequest) {
            return;
          }

          const freshState = getState();
          const currentGroupBy = freshState.groupBy.globalGroupBy;

          if (currentGroupBy?.property) {
            const matchingColumn = findMatchingColumnInCategories(
              list,
              currentGroupBy.property
            );

            if (
              matchingColumn &&
              shouldUpdateGlobalGroupBy(currentGroupBy, matchingColumn)
            ) {
              dispatch(
                setGlobalGroupBy({
                  ...currentGroupBy,
                  title: buildEnrichedTitle(
                    matchingColumn.title,
                    matchingColumn,
                    currentGroupBy,
                    kind
                  ),
                  units: matchingColumn.units,
                  defaultUnit: matchingColumn.defaultUnit
                })
              );
            }
          }
        } else if (kind === EntityKind.Facility) {
          dispatch(setFacilityFields(list));

          // Skip sync if a newer request has been initiated (prevents race conditions)
          const isStaleRequest = currentRequestId !== latestCategoryListRequestId[kind];
          if (isStaleRequest) {
            return;
          }

          // Sync globalFacilityFocus with fresh category data to ensure correct unit display
          const freshState = getState();
          const currentFacilityFocus = freshState.groupBy.globalFacilityFocus;

          if (currentFacilityFocus?.property) {
            const matchingColumn = findMatchingColumnInCategories(
              list,
              currentFacilityFocus.property
            );

            if (
              matchingColumn &&
              shouldUpdateGlobalGroupBy(currentFacilityFocus, matchingColumn)
            ) {
              dispatch(
                setGlobalGroupBy({
                  ...currentFacilityFocus,
                  title: buildEnrichedTitle(
                    matchingColumn.title,
                    matchingColumn,
                    currentFacilityFocus,
                    kind
                  ),
                  units: matchingColumn.units,
                  defaultUnit: matchingColumn.defaultUnit
                })
              );
            }
          }
        }
      }
      dispatch(setRefreshing(false));
    } catch (err) {
      dispatch(setRefreshing(false));
      toast.error("Unable to fetch focus fields.");
      Sentry.withScope((scope) => {
        scope.setContext("getCategoryList", {
          entityKind: kind.toString(),
          unitType,
          operation: "fetch_category_list"
        });
        scope.setLevel("error");
        Sentry.captureException(err);
      });
    }
  };

export const updateProjectColumns =
  (entityKind = EntityKind.Well, projectId?: string): AppThunk =>
  async (dispatch, getState) => {
    const selectedProjectId = projectId ?? getState().groupBy.selectedProjectIdForColumns;
    const projectList = await fetchProjectDefinedColumns(entityKind, selectedProjectId);

    if (projectList) {
      if (entityKind === EntityKind.Well) {
        dispatch(setProjectDefinedColumns(projectList));
      } else {
        dispatch(setFacilityProjectDefinedColumns(projectList));
      }
    }
  };

export const updateGroupByTitle =
  (title: string): AppThunk =>
  (dispatch) => {
    dispatch(setGroupByTitle(title));
  };

export default groupBySlice.reducer;
